import { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import { useAuth } from '@/contexts/AuthContext';
import { getAuthToken, getRefreshToken } from '@/utils/token';
import * as Yup from 'yup';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Link,
  Checkbox,
  FormControlLabel,
  Paper,
  Fade,
  CircularProgress,
  Divider,
  useTheme,
  alpha
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Person as PersonIcon,
  Lock as LockIcon,
  MedicalServices as MedicalServicesIcon,
  Google as GoogleIcon,
  Facebook as FacebookIcon,
  Apple as AppleIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import type { Variants } from 'framer-motion';

const containerVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.4, 0, 0.2, 1]
    }
  }
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 10 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.5,
      ease: [0.4, 0, 0.2, 1]
    }
  })
};

const NewLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { login: loginUser, isAuthenticated } = useAuth();

  // Redirect if already authenticated
  useEffect(() => {
    const checkAuth = () => {
      // Don't redirect if we're already on the login page
      if (location.pathname === '/login') {
        return;
      }
      
      const token = getAuthToken();
      const user = localStorage.getItem('user');
      
      // Only redirect if we have both a valid token and user data
      if (token && user) {
        try {
          // Make sure the user data is valid JSON
          const userData = JSON.parse(user);
          if (userData?.id) {
            const from = (location.state as any)?.from?.pathname || '/dashboard';
            // Only navigate if we're not already on the target page
            if (location.pathname !== from) {
              console.log('[AUTH] Already authenticated, redirecting to:', from);
              navigate(from, { replace: true });
            }
          }
        } catch (e) {
          console.error('Error parsing user data:', e);
          // Clear invalid user data
          localStorage.removeItem('user');
        }
      }
    };
    
    checkAuth();
  }, [navigate, location, location.pathname]);

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .required('Email is required')
        .email('Please enter a valid email address'),
      password: Yup.string()
        .required('Password is required')
        .min(8, 'Password must be at least 8 characters'),
    }),
    onSubmit: async (values, { setSubmitting, setFieldError }) => {
      try {
        console.log('Starting login process...');
        setIsLoading(true);
        setError('');
        
        // Clear any previous errors
        setFieldError('email', '');
        setFieldError('password', '');
        
        // Call the login function from AuthContext with email
        const user = await loginUser(values.email, values.password, 'email');
        
        if (!user) {
          throw new Error('No user data returned from server');
        }
        
        console.log('Login successful, user:', user);
        
        // Get all auth details for debugging
        const token = getAuthToken();
        const refreshToken = getRefreshToken();
        const authDetails = {
          isAuthenticated: true, // Force true since login was successful
          token,
          tokenLength: token?.length,
          refreshToken,
          refreshTokenLength: refreshToken?.length,
          user: localStorage.getItem('user'),
          expiresAt: localStorage.getItem('expiresAt'),
          redirectTo: location.state?.from?.pathname || '/dashboard',
          timestamp: new Date().toISOString()
        };
        
        // Log detailed token information
        console.log('[LOGIN] Auth details after login:', {
          ...authDetails,
          tokenPreview: authDetails.token ? `${authDetails.token.substring(0, 10)}...${authDetails.token.substring(authDetails.token.length - 10)}` : 'no token',
          refreshTokenPreview: authDetails.refreshToken ? `${authDetails.refreshToken.substring(0, 10)}...${authDetails.refreshToken.substring(authDetails.refreshToken.length - 10)}` : 'no refresh token'
        });
        
        // Verify token is properly set before redirect
        const tokenAfterLogin = getAuthToken();
        if (!tokenAfterLogin) {
          console.error('[LOGIN] CRITICAL: Token is missing from localStorage after login!');
          throw new Error('Authentication failed: Token not stored properly');
        }
        
        // Get the redirect path from location state or default to dashboard
        const from = (location.state as any)?.from?.pathname || '/dashboard';
        console.log('[LOGIN] Login successful, redirecting to:', from);
        
        // Force a small delay to ensure all state updates are processed
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Force a hard redirect to ensure the app state is fully updated
        window.location.href = from;
      } catch (err: any) {
        console.error('Login error:', err);
        
        // Handle different types of errors
        if (err.response?.data?.message) {
          setError(err.response.data.message);
        } else if (err.message.includes('Network Error')) {
          setError('Unable to connect to the server. Please check your internet connection.');
        } else if (err.message.includes('401')) {
          setError('Invalid username or password. Please try again.');
          setFieldError('password', 'Invalid credentials');
        } else {
          setError(err.message || 'An unexpected error occurred. Please try again later.');
        }
      } finally {
        setIsLoading(false);
        setSubmitting(false);
      }
    },
  });

  const toggleShowPassword = () => setShowPassword(!showPassword);
  const toggleRememberMe = () => setRememberMe(!rememberMe);

  const handleSocialLogin = (provider: string) => {
    // Implement social login logic here
    console.log(`Logging in with ${provider}`);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.dark} 100%)`,
        p: 3,
      }}
    >
      <Fade in={true} timeout={800}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Paper
            elevation={10}
            sx={{
              width: '100%',
              maxWidth: 460,
              borderRadius: 4,
              overflow: 'hidden',
              boxShadow: theme.shadows[10],
            }}
          >
          {/* Header Section */}
          <Box
            sx={{
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              p: 3,
              textAlign: 'center',
            }}
          >
            <MedicalServicesIcon sx={{ fontSize: 64, mb: 1 }} />
            <Typography variant="h4" component="h1" fontWeight="bold">
              Welcome Back
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.9, mt: 1 }}>
              Sign in to your account to continue
            </Typography>
          </Box>

          
          {/* Main Content */}
          <Box sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
            {/* Error Alert */}
            {error && (
              <motion.div
                custom={0}
                variants={itemVariants}
              >
                <Alert
                  severity="error"
                  sx={{ mb: 3, borderRadius: 2 }}
                >
                  {error}
                </Alert>
              </motion.div>
            )}

            {/* Login Form */}
            <Box
              component="form"
              onSubmit={formik.handleSubmit}
              noValidate
            >
              {/* Username Field */}
              <Box mb={3} component={motion.div} custom={1} variants={itemVariants}>
                <TextField
                  fullWidth
                  id="email"
                  name="email"
                  label="Email"
                  type="email"
                  variant="outlined"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.common.white, 0.9),
                    },
                  }}
                />
              </Box>

              {/* Password Field */}
              <Box mb={2} component={motion.div} custom={2} variants={itemVariants}>
                <TextField
                  fullWidth
                  id="password"
                  name="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  variant="outlined"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.password && Boolean(formik.errors.password)}
                  helperText={formik.touched.password && formik.errors.password}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={toggleShowPassword}
                          edge="end"
                          size="large"
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.common.white, 0.9),
                    },
                  }}
                />
              </Box>

              {/* Remember Me & Forgot Password */}
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rememberMe}
                      onChange={toggleRememberMe}
                      color="primary"
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2" color="textSecondary">
                      Remember me
                    </Typography>
                  }
                />
                <Link
                  component={RouterLink}
                  to="/forgot-password"
                  variant="body2"
                  color="primary"
                  sx={{ textDecoration: 'none', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
                >
                  Forgot password?
                </Link>
              </Box>

              {/* Submit Button */}
              <Box mb={3} component={motion.div} custom={4} variants={itemVariants}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={isLoading || formik.isSubmitting}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 'bold',
                    textTransform: 'none',
                    fontSize: '1rem',
                    boxShadow: '0 4px 14px 0 rgba(0, 118, 255, 0.39)',
                    '&:hover': {
                      boxShadow: '0 6px 20px 0 rgba(0, 118, 255, 0.5)',
                    },
                  }}
                >
                  {isLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Sign In'
                  )}
                </Button>
              </Box>
            </Box>

            {/* Divider */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                my: 3,
              }}
              component={motion.div}
              custom={4}
              variants={itemVariants}
            >
              <Divider sx={{ flexGrow: 1 }} />
              <Typography variant="body2" color="textSecondary" sx={{ mx: 2 }}>
                OR CONTINUE WITH
              </Typography>
              <Divider sx={{ flexGrow: 1 }} />
            </Box>            
          </Box>
          </Paper>
        </motion.div>
      </Fade>
    </Box>
  );
};

export default NewLogin;
